import React, { useContext, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { TableRow, TableCell, IconButton, Collapse, Menu, MenuItem } from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp, MoreVert } from '@material-ui/icons';
import { css } from '@emotion/react';
import * as Sentry from '@sentry/react';
import { OnwardChip } from '@/styles/blocks';
import { UserContext } from '@/components/App';
import { greens, greys, reds } from '@/styles/colors';
import { AIRPORT_CODES_BY_ZIP } from '@/constants/airportCodes';
import ShipperSection from './ShipperSection';
import { CollapseCell, ExpandedContent, CopyableId } from './blocks';
import { DocActionPill } from './DocStatusIcon';
import DocumentUploadModal from './DocumentUploadModal';
import { CARRIER_TYPE_LABELS, SUPPLY_CHAIN_LEG_LABELS } from './constants';
import ConnectShipperModal from './ConnectShipperModal';
import { CorpAdminContext, MARKETPLACE_KEY } from '../store';
import { DISCONNECT_CARRIER, GET_CORP_INTEGRATION_OVERRIDES, GET_CORP_PRICING_OVERRIDES } from '../graphql';
import OLogo from '@/assets/OLogo_Transparent.png';

const ORG_DOC_CELLS = [
    { type: 'AGREEMENT', label: 'Agreement' },
    { type: 'COI', label: 'Insurance' },
    { type: 'W9', label: 'W9' },
];

const TYPE_WIDTH = 120;
const LEG_WIDTH = 160;
const AIRPORT_CODES_WIDTH = 200;
const DOCUMENTS_WIDTH = 220;
const ACTIONS_WIDTH_NETWORK = 80;
const ACTIONS_WIDTH_FLEX = 80;

const fixedWidth = (width) => css`
    width: ${width}px;
    min-width: ${width}px;
    max-width: ${width}px;
`;

const CarrierRow = ({ carrier_id, carrierData, assetType, onSignIn, onDetails, isExpanded, onToggleExpand }) => {
    const { admin_clients } = useContext(UserContext);
    const { state, callbacks } = useContext(CorpAdminContext);
    const { corpDocsByCarrier } = state;
    const { setNotification } = callbacks;
    const [modalCtx, setModalCtx] = useState(null);
    const [connectShipperOpen, setConnectShipperOpen] = useState(false);
    const [actionsAnchor, setActionsAnchor] = useState(null);
    const open = !!isExpanded;
    const { carrier } = carrierData;
    const isMarketplace = carrier_id === MARKETPLACE_KEY;
    const isNetworkCarrier = assetType === 'light';
    const showSignIn = !isMarketplace && !isNetworkCarrier;
    const showConnectShipper = true;
    const showDelete = !isMarketplace;
    // Every carrier the admin works with has an identifiers surface; marketplace rows aren't theirs.
    const showDetails = !isMarketplace;
    const hasActions = showSignIn || showConnectShipper || showDelete || showDetails;
    const colSpan = isNetworkCarrier ? 5 : 4;

    const orgShipperIds = useMemo(
        () => (admin_clients || []).filter((c) => !!c.user?.roles?.['SHIPPER']).map((c) => c.client_id),
        [admin_clients]
    );

    const [disconnectCarrier, { loading: disconnecting }] = useMutation(DISCONNECT_CARRIER, {
        update(cache) {
            // Strip the deleted integration_overrides for this carrier × org shippers.
            try {
                const existingOverrides = cache.readQuery({
                    query: GET_CORP_INTEGRATION_OVERRIDES,
                    variables: { orgShipperIds },
                });
                if (existingOverrides) {
                    const orgSet = new Set(orgShipperIds);
                    cache.writeQuery({
                        query: GET_CORP_INTEGRATION_OVERRIDES,
                        variables: { orgShipperIds },
                        data: {
                            integration_overrides: existingOverrides.integration_overrides.filter(
                                (o) => !(o.carrier_id === carrier_id && orgSet.has(o.shipper_id))
                            ),
                        },
                    });
                }
            } catch {
                // cache miss is non-fatal
            }
            // Drop matching pricing_overrides from the cached list. The mutation soft-deletes
            // them via enabled: false; the store filters disabled rows out, but the list-of-refs
            // in this query's cache entry doesn't auto-shrink when entity scalars flip — so we
            // remove them explicitly here to keep the Network Carriers grid in sync without a
            // refetch.
            try {
                const existingPricing = cache.readQuery({
                    query: GET_CORP_PRICING_OVERRIDES,
                    variables: { orgShipperIds },
                });
                if (existingPricing) {
                    const orgSet = new Set(orgShipperIds);
                    cache.writeQuery({
                        query: GET_CORP_PRICING_OVERRIDES,
                        variables: { orgShipperIds },
                        data: {
                            pricing_overrides: existingPricing.pricing_overrides.filter((p) => {
                                const matches =
                                    (p.client_id === carrier_id && orgSet.has(p.partner_client_id)) ||
                                    (orgSet.has(p.client_id) && p.partner_client_id === carrier_id) ||
                                    (orgSet.has(p.client_id) && p.assign_id === carrier_id);
                                return !matches;
                            }),
                        },
                    });
                }
            } catch {
                // cache miss is non-fatal
            }
        },
    });

    const closeActions = (e) => {
        e?.stopPropagation?.();
        setActionsAnchor(null);
    };
    const openActions = (e) => {
        e.stopPropagation();
        setActionsAnchor(e.currentTarget);
    };
    const handleSignIn = (e) => {
        e?.stopPropagation?.();
        closeActions();
        onSignIn(carrier);
    };
    const handleConnectShipper = (e) => {
        e?.stopPropagation?.();
        closeActions();
        setConnectShipperOpen(true);
    };
    const handleDetails = (e) => {
        e?.stopPropagation?.();
        closeActions();
        onDetails?.({
            context: isNetworkCarrier ? 'NETWORK' : 'FLEX',
            clientId: carrier_id,
            clientName: displayName,
        });
    };
    const handleDelete = async (e) => {
        e?.stopPropagation?.();
        closeActions();
        if (!window.confirm('Delete all integrations and disable pricing for this carrier?')) return;
        try {
            await disconnectCarrier({ variables: { carrier_id, org_shipper_ids: orgShipperIds } });
            setNotification?.({ severity: 'success', message: 'Carrier disconnected' });
        } catch (err) {
            Sentry.captureException(err);
            setNotification?.({ severity: 'error', message: 'Failed to disconnect carrier' });
        }
    };

    const displayName = isMarketplace ? 'Onward Marketplace' : carrier?.business_name || '--';
    const displayEmail = isMarketplace ? '--' : carrier?.email || '--';
    const displayId = isMarketplace ? '--' : carrier_id;

    const shipperKeys = Object.keys(carrierData)
        .filter((k) => k !== 'carrier')
        .sort((a, b) => {
            const nameA = (admin_clients || []).find((c) => c.client_id === a)?.business_name || '';
            const nameB = (admin_clients || []).find((c) => c.client_id === b)?.business_name || '';
            return nameA.localeCompare(nameB);
        });

    const carrierDocs = corpDocsByCarrier?.[carrier_id] || {};

    // A leg is "active" (green) when the carrier has at least one pricing_override (where it's the
    // client_id) whose override_type matches that leg — same semantics as the supply_chain_legs
    // backfill in migration 1777613055039_self_serve.
    const activeLegSet = useMemo(() => {
        const set = new Set();
        Object.entries(carrierData || {}).forEach(([key, value]) => {
            if (key === 'carrier') return;
            (value?.carrierPricing || []).forEach((p) => {
                if (p?.override_type) set.add(p.override_type);
            });
        });
        return set;
    }, [carrierData]);

    // Airport codes are derived from each carrier location's business_zip via
    // AIRPORT_CODES_BY_ZIP — same approach as the Onward Admin AdminUsers view.
    const airportCodes = useMemo(() => {
        const codes = [];
        (carrier?.locations || []).forEach((location) => {
            const code = AIRPORT_CODES_BY_ZIP[location.business_zip];
            if (code && !codes.includes(code)) codes.push(code);
        });
        return codes;
    }, [carrier]);

    const handleDocCellClick = (e, type, label) => {
        e.stopPropagation();
        if (isMarketplace) return;
        setModalCtx({ type, label, existingDoc: carrierDocs[type] || null });
    };

    return (
        <>
            <TableRow
                onClick={() => {
                    if (window.getSelection().toString()) return;
                    onToggleExpand?.();
                }}
                css={css`
                    cursor: pointer;
                    :hover {
                        background-color: ${greys.selected};
                    }
                `}
            >
                <TableCell>
                    {isNetworkCarrier ? (
                        <div
                            css={css`
                                display: flex;
                                align-items: center;
                                gap: 0.75rem;
                                min-width: 0;
                            `}
                        >
                            {isMarketplace ? (
                                <img
                                    src={OLogo}
                                    alt=""
                                    css={css`
                                        width: 40px;
                                        height: 40px;
                                        border-radius: 4px;
                                        object-fit: contain;
                                        flex-shrink: 0;
                                        background: #fff;
                                        border: 1px solid ${greys[5]};
                                        padding: 4px;
                                    `}
                                />
                            ) : carrier?.profile_img ? (
                                <img
                                    src={carrier.profile_img}
                                    alt=""
                                    style={{ background: carrier.profile_img_color || '#fff' }}
                                    css={css`
                                        width: 40px;
                                        height: 40px;
                                        border-radius: 4px;
                                        object-fit: contain;
                                        flex-shrink: 0;
                                        border: 1px solid ${greys[5]};
                                    `}
                                />
                            ) : (
                                <div
                                    css={css`
                                        width: 40px;
                                        height: 40px;
                                        border-radius: 4px;
                                        flex-shrink: 0;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        background: ${greys[5]};
                                        color: ${greys[2]};
                                        font-weight: 600;
                                    `}
                                >
                                    {(displayName?.[0] || '?').toUpperCase()}
                                </div>
                            )}
                            <div
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                    gap: 2px;
                                    min-width: 0;
                                    flex: 1;
                                `}
                            >
                                <span
                                    css={css`
                                        font-weight: 600;
                                    `}
                                >
                                    {displayName}
                                </span>
                                {!isMarketplace && (
                                    <div
                                        css={css`
                                            display: flex;
                                            flex-direction: column;
                                            align-items: flex-start;
                                            gap: 4px;
                                        `}
                                    >
                                        <CopyableId label="Primary Email" value={displayEmail} />
                                        <CopyableId label="Account ID" value={displayId} />
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        displayName
                    )}
                </TableCell>
                {isNetworkCarrier ? (
                    <>
                        <TableCell css={fixedWidth(TYPE_WIDTH)}>
                            {isMarketplace || !carrier?.carrier_type ? (
                                <span
                                    css={css`
                                        color: ${greys[5]};
                                    `}
                                >
                                    —
                                </span>
                            ) : (
                                CARRIER_TYPE_LABELS[carrier.carrier_type] || carrier.carrier_type
                            )}
                        </TableCell>
                        <TableCell css={fixedWidth(LEG_WIDTH)}>
                            {isMarketplace || !carrier?.supply_chain_legs?.length ? (
                                <span
                                    css={css`
                                        color: ${greys[5]};
                                    `}
                                >
                                    —
                                </span>
                            ) : (
                                <div
                                    css={css`
                                        display: flex;
                                        flex-wrap: wrap;
                                        gap: 4px;
                                    `}
                                >
                                    {(carrier.supply_chain_legs.includes('E2E')
                                        ? carrier.supply_chain_legs.filter((leg) => leg !== 'FINAL_MILE')
                                        : carrier.supply_chain_legs
                                    ).map((leg) => (
                                        <OnwardChip
                                            key={leg}
                                            size="small"
                                            label={SUPPLY_CHAIN_LEG_LABELS[leg] || leg}
                                            style={
                                                activeLegSet.has(leg)
                                                    ? {
                                                          backgroundColor: `${greens.primary}24`,
                                                          color: greens.primary,
                                                      }
                                                    : undefined
                                            }
                                        />
                                    ))}
                                </div>
                            )}
                        </TableCell>
                        <TableCell css={fixedWidth(AIRPORT_CODES_WIDTH)}>
                            {isMarketplace || airportCodes.length === 0 ? (
                                <span
                                    css={css`
                                        color: ${greys[5]};
                                    `}
                                >
                                    —
                                </span>
                            ) : (
                                <div
                                    css={css`
                                        display: flex;
                                        flex-wrap: wrap;
                                        gap: 4px;
                                    `}
                                >
                                    {airportCodes.map((code) => (
                                        <OnwardChip key={code} size="small" label={code} />
                                    ))}
                                </div>
                            )}
                        </TableCell>
                        <TableCell css={fixedWidth(DOCUMENTS_WIDTH)}>
                            {isMarketplace ? (
                                <span
                                    css={css`
                                        color: ${greys[5]};
                                    `}
                                >
                                    —
                                </span>
                            ) : (
                                <div
                                    css={css`
                                        display: flex;
                                        flex-direction: column;
                                        align-items: stretch;
                                        gap: 4px;

                                        & > button {
                                            display: flex;
                                            width: 100%;
                                            justify-content: space-between;
                                        }
                                    `}
                                >
                                    {ORG_DOC_CELLS.map(({ type, label }) => (
                                        <DocActionPill
                                            key={type}
                                            doc={carrierDocs[type]}
                                            label={label}
                                            prefixLabel={label}
                                            onClick={(e) => handleDocCellClick(e, type, label)}
                                        />
                                    ))}
                                </div>
                            )}
                        </TableCell>
                    </>
                ) : (
                    <>
                        <TableCell>
                            {isMarketplace ? (
                                <span>{displayEmail}</span>
                            ) : (
                                <CopyableId label="Primary Email" value={displayEmail} />
                            )}
                        </TableCell>
                        <TableCell>
                            {isMarketplace ? (
                                <span>{displayId}</span>
                            ) : (
                                <CopyableId label="Account ID" value={displayId} />
                            )}
                        </TableCell>
                    </>
                )}
                <TableCell
                    align="right"
                    css={css`
                        ${fixedWidth(isNetworkCarrier ? ACTIONS_WIDTH_NETWORK : ACTIONS_WIDTH_FLEX)};
                        white-space: nowrap;
                    `}
                >
                    {hasActions && (
                        <>
                            <IconButton size="small" onClick={openActions}>
                                <MoreVert />
                            </IconButton>
                            <Menu
                                anchorEl={actionsAnchor}
                                open={!!actionsAnchor}
                                onClose={closeActions}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                getContentAnchorEl={null}
                            >
                                {showDetails && <MenuItem onClick={handleDetails}>Details</MenuItem>}
                                {showSignIn && <MenuItem onClick={handleSignIn}>Sign In</MenuItem>}
                                {showConnectShipper && (
                                    <MenuItem onClick={handleConnectShipper}>Connect Shipper</MenuItem>
                                )}
                                {showDelete && (
                                    <MenuItem
                                        onClick={handleDelete}
                                        disabled={disconnecting}
                                        css={css`
                                            color: ${reds.primary};
                                        `}
                                    >
                                        Delete
                                    </MenuItem>
                                )}
                            </Menu>
                        </>
                    )}
                    <IconButton size="small">{open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <CollapseCell colSpan={colSpan}>
                    <Collapse in={open} unmountOnExit>
                        <ExpandedContent>
                            {shipperKeys.map((shipper_id) => (
                                <ShipperSection
                                    key={shipper_id}
                                    carrier_id={carrier_id}
                                    carrierLegs={carrier?.supply_chain_legs}
                                    shipper_id={shipper_id}
                                    data={carrierData[shipper_id]}
                                    assetType={assetType}
                                />
                            ))}
                        </ExpandedContent>
                    </Collapse>
                </CollapseCell>
            </TableRow>
            <ConnectShipperModal
                open={connectShipperOpen}
                onClose={() => setConnectShipperOpen(false)}
                carrier_id={isMarketplace ? null : carrier_id}
                connectedShipperIds={new Set(shipperKeys)}
            />
            {modalCtx && (
                <DocumentUploadModal
                    open={!!modalCtx}
                    onClose={() => setModalCtx(null)}
                    type={modalCtx.type}
                    client_id={carrier_id}
                    existingDoc={modalCtx.existingDoc}
                    contextLabel={displayName}
                />
            )}
        </>
    );
};

export default CarrierRow;
